import { normalizeProductLanguage } from "@/lib/product-language";

export type ProductLinkTarget =
  | { kind: "internal"; href: string }
  | { kind: "external"; href: string };

const productIdPattern = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const unsafeUrlCharacters = /[\s\u0000-\u001F\u007F]/;
export const PRODUCT_SOURCE_CODE = { taobao: "1", "1688": "2" } as const;

export function marketplaceProductHref(goodsId: string, source: keyof typeof PRODUCT_SOURCE_CODE) {
  if (!/^\d{6,30}$/.test(goodsId)) return null;
  return `/product?goodsId=${encodeURIComponent(goodsId)}&source=${PRODUCT_SOURCE_CODE[source]}`;
}

export function validProductId(value: unknown) {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return productIdPattern.test(normalized) ? normalized : null;
}

export function validProductSourceUrl(value: unknown) {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  if (!normalized || unsafeUrlCharacters.test(normalized)) return null;
  try {
    const url = new URL(normalized);
    if (!["http:", "https:"].includes(url.protocol) || !url.hostname || url.username || url.password) return null;
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
}

export function resolveProductLink(input: { productId?: unknown; sourceUrl?: unknown }): ProductLinkTarget | null {
  const productId = validProductId(input.productId);
  if (productId) return { kind: "internal", href: `/product/${encodeURIComponent(productId)}` };
  const sourceUrl = validProductSourceUrl(input.sourceUrl);
  return sourceUrl ? { kind: "external", href: sourceUrl } : null;
}

export function offerIdFrom1688Link(input: string) {
  const value = input.trim();
  if (/^\d{6,20}$/.test(value)) return value;
  try {
    const url = new URL(value);
    if (!url.hostname.endsWith("1688.com")) return null;
    const query = url.searchParams.get("offerId");
    return query && /^\d{6,20}$/.test(query) ? query : url.pathname.match(/\/offer\/(\d{6,20})\.html/i)?.[1] ?? null;
  } catch {
    return null;
  }
}

export function taobaoProductUrl(input: string) {
  const value = input.trim();
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const marketplaceHost = host === "taobao.com" || host.endsWith(".taobao.com") || host === "tmall.com" || host.endsWith(".tmall.com");
    if (url.protocol !== "https:" || (!marketplaceHost && host !== "distributor.taobao.global")) return null;
    return value;
  } catch {
    return null;
  }
}

export function taobaoLookupFromText(input: string) {
  const value = input.trim();
  const itemUrl = taobaoProductUrl(value);
  if (itemUrl) return { itemUrl };
  const miId = value.match(/^mi_id\s*[:=]\s*(\S+)$/i)?.[1];
  if (miId) return { miId };
  if (/^tao_password\s*[:=]/i.test(value)) return { taoPassword: value.replace(/^tao_password\s*[:=]\s*/i, "") };
  if (/([￥₤€$])[^￥₤€$\r\n]{4,}\1/.test(value)) return { taoPassword: value };
  return null;
}

export function productSearchDestination(input: string, siteLanguage = "en") {
  const value = input.trim();
  const language = normalizeProductLanguage(siteLanguage) ?? "en";
  const offerId = offerIdFrom1688Link(value);
  if (offerId) return `${marketplaceProductHref(offerId, "1688")}&language=${language}`;
  const taobao = taobaoLookupFromText(value);
  if (taobao) {
    const params = new URLSearchParams();
    if (taobao.itemUrl) {
      try {
        const itemId = new URL(taobao.itemUrl).searchParams.get("id");
        if (itemId && /^\d{6,30}$/.test(itemId)) return `${marketplaceProductHref(itemId, "taobao")}&language=${language}`;
      } catch { /* Fall through to the lookup-compatible route. */ }
      params.set("item_url", taobao.itemUrl);
    }
    if (taobao.miId) params.set("mi_id", taobao.miId);
    if (taobao.taoPassword) params.set("tao_password", taobao.taoPassword);
    params.set("language", language);
    return `/product/taobao?${params}`;
  }
  return `/diy-order?item=${encodeURIComponent(value)}`;
}
